import {createContext, useCallback, useContext, useEffect} from 'react';

import {useLocation, useNavigate} from 'react-router-dom';

import {PASSWORD_EVENTS} from '@/app/auth';
import {useLinkExpiredTrackers} from '@/app/main/authentication/pages/LinkExpired/trackers';
import {DOWNLOAD_APP_URL} from '@/app/main/onboarding/pages/DownloadApp/models/constants';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {AuthErrorNames} from '@/services/amplify/AmplifyConstants';

interface ILinkExpiredViewModel {
  handleOpenDownloadApp: () => void;
  showVariant: boolean;
}

const LinkExpiredContext = createContext<ILinkExpiredViewModel | undefined>(
  undefined,
);

interface LinkExpiredLocationState {
  internal_error?: string;
  code?: string;
  link_sent_at?: string;
  email?: string;
}

export const LinkExpiredProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const {trackViewLinkExpired} = useLinkExpiredTrackers();
  const navigate = useNavigate();

  const {isMobile} = useWindowSize();
  const location = useLocation();

  const state = location.state as LinkExpiredLocationState | undefined;

  const showVariant =
    state?.internal_error === AuthErrorNames.ExpiredCodeException;

  const handleOpenDownloadApp = useCallback(() => {
    isMobile
      ? window.open(DOWNLOAD_APP_URL, '_blank')
      : navigate('/authentication/sign-up');
  }, [isMobile, navigate]);

  useEffect(() => {
    trackViewLinkExpired(PASSWORD_EVENTS.VIEW_LINK_EXPIRED_SCREEN, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LinkExpiredContext.Provider value={{handleOpenDownloadApp, showVariant}}>
      {children}
    </LinkExpiredContext.Provider>
  );
};

export const useLinkExpiredViewModel = () => {
  const ctx = useContext(LinkExpiredContext);
  if (!ctx)
    throw new Error(
      'useLinkExpiredViewModel must be used inside LinkExpiredProvider',
    );
  return ctx;
};
